// Traceability model shared by the detail page and the Excel export, so both
// always show the same chain, loss, schema comparison and destinations.

// Destination status (FORWARD), in display order
export const STATUS_LABELS = {
  'Sudah Keluar': 'Sudah Dikirim',
  'Hapus - Jual': 'Terjual',
  // turned into another item (production); its own tree shows the rest
  Diproses: 'Diproses jadi barang lain',
  'Dalam Stok': 'Masih di Gudang',
  'Hapus - Buang': 'Dibuang',
}
export const STATUS_ORDER = Object.keys(STATUS_LABELS)
// Statuses where someone else received the goods (sent or sold)
const RECEIVED_STATUSES = ['Sudah Keluar', 'Hapus - Jual']

// Methods created by the system itself (no production schema behind them)
export const SYSTEM_METHODS = ['SPLIT']

export const normalizeUnit = (u) =>
  String(u ?? '')
    .trim()
    .toLowerCase()

export const unitOf = (x) => x?.unit || x?.item?.unit || ''

// Units match when either side is not set
export const unitMatches = (a, b) =>
  !a || !b || normalizeUnit(a) === normalizeUnit(b)

// "12,34" -> [12, 34]
export const parseIds = (value) =>
  String(value ?? '')
    .split(',')
    .map((x) => Number(x))
    .filter((x) => x > 0)

export const parseExtraData = (s) => {
  try {
    return JSON.parse(s?.extraData) || {}
  } catch {
    return {}
  }
}

// Amount of a related (source / destination) inventory
export const linkedAmount = (s) =>
  Number(s?.usedAmount ?? s?.initialAmount ?? s?.amount) || 0

// Sum amounts per item (itemId when available, otherwise name) and unit.
// Different units are kept apart because they cannot be added together.
export const addToBreakdown = (map, { itemId, name, unit, amount }) => {
  const key = `${itemId ?? name}__${normalizeUnit(unit)}`
  const existing = map.get(key)
  if (existing) {
    existing.amount += amount
  } else {
    map.set(key, { itemId, name, unit, amount })
  }
}

const itemKeyOf = (x) =>
  `${x?.item?.id ?? x?.itemId ?? ''}__${normalizeUnit(unitOf(x))}`

// First source of a material: walk up while the parent is the same item and
// unit (split, transfer, ...); an item change is an earlier production step.
// getNode: id -> traced inventory (or undefined when not traced)
const firstSourceOf = (node, getNode) => {
  let current = node
  for (let guard = 0; guard < 100; guard += 1) {
    const parents = parseIds(current?.parentInventoryIds)
      .map(getNode)
      .filter(Boolean)
    if (parents.length !== 1 || itemKeyOf(parents[0]) !== itemKeyOf(current))
      break
    current = parents[0]
  }
  return current
}

// Schema of the process (label) that made `node`, when it fits its unit
const productionSchema = (schemas, node, label) => {
  if (!schemas || !label || SYSTEM_METHODS.includes(label)) return null
  const sc = schemas[`${node?.item?.id ?? node?.itemId}__${label}`]
  return sc?.status === 'ok' &&
    sc.exact &&
    sc.outputAmount > 0 &&
    unitMatches(sc.outputUnit, unitOf(node))
    ? sc
    : null
}

// Schema input of `source` for `output` units of the product
const schemaNeed = (sc, source, output) => {
  const input = sc?.inputs.find(
    (i) =>
      Number(i.itemId) === Number(source?.item?.id ?? source?.itemId) &&
      unitMatches(i.unit, unitOf(source)),
  )
  return input?.reqAmount ? (input.reqAmount / sc.outputAmount) * output : null
}

// First-place sources of `qty` of an inventory, with how much of each went
// into it. A material that was itself made from other items is replaced by
// what it was made from (x,y -> z then o,p,z -> a gives x, y, o and p), and
// only the share of an intermediate that went on counts: when 4 of 8 made
// went into this item, half of its material does.
// What a parent gave is its Jumlah Dipakai; when the parent also went to
// other inventories of the trace that number is for another one, so the
// schema of the process decides (its need minus what the other parents of
// that item gave). `standard` is the schema amount for the same share.
// Without a schema there is no exact answer (the API keeps one Jumlah
// Dipakai per inventory, not per parent -> child): the amount is capped by
// what the parent had left after its other pieces in the trace, and the
// source is flagged `estimated`.
//   nodes: id -> traced inventory, relationOf: id -> summaryInvData entry
// Returns [{ s, amount, standard, method, estimated }]: standard is null
// without a schema, method is the process the source went into
export const sourceAmounts = (inv, qty, nodes, relationOf, schemas) => {
  const getNode = (id) => nodes.get(Number(id))
  const all = [inv, ...nodes.values()]
  const childCount = (id) =>
    all.filter((n) => parseIds(n?.parentInventoryIds).includes(Number(id)))
      .length
  const usedOf = (p) => Number(relationOf(p.id)?.amountUsed) || 0
  const result = new Map()
  const add = (s, amount, standard, method, estimated) => {
    const row = result.get(Number(s.id)) || {
      s,
      amount: 0,
      standard: 0,
      method,
      estimated: false,
    }
    row.amount += amount
    row.estimated = row.estimated || estimated
    row.standard =
      row.standard == null || standard == null ? null : row.standard + standard
    result.set(Number(s.id), row)
  }

  // `node` was made by a process; `take` (actual) and `takeStd` (schema) of
  // its `output` went on to the finished good
  const walk = (node, output, take, takeStd, depth, estimated) => {
    if (depth > 50) return
    const parents = parseIds(node?.parentInventoryIds)
      .map(getNode)
      .filter(Boolean)
    const label = parents
      .map((p) => relationOf(p.id)?.createMethodLabel)
      .find((m) => m && !SYSTEM_METHODS.includes(m))
    const sc = productionSchema(schemas, node, label)
    const share = output > 0 ? take / output : 0
    const known = (p) => childCount(p.id) <= 1
    const guessed = new Set()
    // No schema: never more than the parent had left after the pieces split
    // off it to the other inventories of the trace
    const leftFor = (p) =>
      (Number(p.initialAmount) || 0) -
      all
        .filter(
          (c) =>
            c !== node &&
            itemKeyOf(c) === itemKeyOf(p) &&
            parseIds(c?.parentInventoryIds).includes(Number(p.id)),
        )
        .reduce((t, c) => t + (Number(c.initialAmount) || 0), 0)
    const given = new Map(
      parents.map((p) => {
        if (known(p)) return [p, usedOf(p)]
        const need = schemaNeed(sc, p, output)
        if (need == null) {
          guessed.add(p)
          return [p, Math.max(0, Math.min(usedOf(p), leftFor(p)))]
        }
        const same = parents.filter((o) => itemKeyOf(o) === itemKeyOf(p))
        const fromKnown = same.filter(known).reduce((t, o) => t + usedOf(o), 0)
        const unknownCount = same.filter((o) => !known(o)).length
        return [p, Math.max(0, need - fromKnown) / unknownCount]
      }),
    )
    parents.forEach((p) => {
      const same = parents.filter((o) => itemKeyOf(o) === itemKeyOf(p))
      const groupGiven = same.reduce((t, o) => t + given.get(o), 0)
      const perUnit = schemaNeed(sc, p, 1)
      const std =
        perUnit == null || takeStd == null
          ? null
          : perUnit *
            takeStd *
            (groupGiven > 0 ? given.get(p) / groupGiven : 1 / same.length)
      // eslint-disable-next-line no-use-before-define
      source(
        p,
        given.get(p) * share,
        std,
        depth,
        label,
        estimated || guessed.has(p),
      )
    })
  }

  // splits and transfers pass the amount on as it is
  const source = (p, amount, std, depth, method, estimated) => {
    const first = firstSourceOf(p, getNode)
    if (parseIds(first?.parentInventoryIds).some(getNode)) {
      walk(
        first,
        Number(first.initialAmount) || 0,
        amount,
        std,
        depth + 1,
        estimated,
      )
    } else {
      add(p, amount, std, method, estimated)
    }
  }

  const start = firstSourceOf(inv, getNode)
  walk(
    start,
    start === inv ? qty : Number(start.initialAmount) || 0,
    qty,
    qty,
    0,
    false,
  )
  return Array.from(result.values())
}

// BACKWARD export rows: the first-place sources of a finished inventory with
// what went into it, like the detail page (see sourceAmounts).
// `firstAmount` is the stock of the first source of that material: walk up
// while the parent is the same item and unit (split, transfer, ...).
// Without traced parents every traced source is returned as before.
export const exportSources = (inv, resolve, schemas) => {
  const nodes = new Map()
  ;(inv?.summaryInvIds || []).forEach((sid) => {
    const s = resolve(sid)
    if (s) nodes.set(Number(s.id), s)
  })
  const relationOf = (id) =>
    (inv?.summaryInvData || []).find((d) => Number(d.id) === Number(id))
  const raw = sourceAmounts(
    inv,
    Number(inv?.amount) || 0,
    nodes,
    relationOf,
    schemas,
  )
  const rows = raw.length
    ? raw
    : Array.from(nodes.values()).map((s) => ({ s, amount: null }))
  return rows.map(({ s, amount, method, estimated }) => ({
    summary: s,
    usedAmount: amount,
    method,
    estimated: !!estimated,
    firstAmount:
      Number(firstSourceOf(s, (pid) => nodes.get(pid)).initialAmount) || 0,
  }))
}

// Neraca bahan per unit: Set and Kg are never added together.
// bahan / jadi: [{ unit, amount }]
export const conversionBalance = (bahanItems, jadiItems) => {
  const units = new Map()
  const add = (list, field) =>
    (list || []).forEach(({ unit, amount }) => {
      const key = normalizeUnit(unit)
      const row = units.get(key) || { unit, bahanMasuk: 0, barangJadi: 0 }
      row[field] += Number(amount) || 0
      units.set(key, row)
    })
  add(bahanItems, 'bahanMasuk')
  add(jadiItems, 'barangJadi')

  const round2 = (n) => Math.round(n * 100) / 100
  return Array.from(units.values()).map((row) => {
    const selisih = row.bahanMasuk - row.barangJadi
    // Only a unit on both sides can be compared (bahan in Kg with goods in
    // Set says nothing about loss): no selisih label or percentage otherwise
    const comparable = row.bahanMasuk > 0 && row.barangJadi > 0
    let balanceLabel = 'Tidak Ada Pembanding'
    if (comparable) {
      balanceLabel =
        Math.abs(selisih) < 0.0001
          ? 'Tidak Ada Selisih'
          : selisih > 0
            ? 'Susut'
            : 'Kelebihan'
    }
    return {
      ...row,
      selisih,
      comparable,
      balanceLabel,
      hasilPct: comparable
        ? round2((row.barangJadi / row.bahanMasuk) * 100)
        : null,
      selisihPct: comparable ? round2((selisih / row.bahanMasuk) * 100) : null,
    }
  })
}

// Map id -> inventory for a laporevents/summary response
export const summaryToMap = (summary) => {
  const map = new Map()
  ;(summary || []).forEach((group) => {
    ;(group?.inventories || []).forEach((inv) => {
      map.set(Number(inv?.id), inv)
    })
  })
  return map
}

// Status of a related inventory, seen from the logged-in user
export const makeStatusHelpers = (userId) => {
  const getStatusLabel = (s) => {
    if (!s || s?.isBlank) return '-'
    if (Number(s.ownerUserId) === 0) {
      return parseExtraData(s)?.buyerName ? 'Hapus - Jual' : 'Hapus - Buang'
    }
    // `ownerUserId` is the current owner. A transfer moves the same
    // inventory to the receiver, and `ownerUserIds` only keeps the
    // ownership history, so it must not be used here.
    return String(s.ownerUserId) === String(userId)
      ? 'Dalam Stok'
      : 'Sudah Keluar'
  }

  // Who / where a destination inventory ended up (FORWARD)
  const getDestinationName = (s) => {
    const status = getStatusLabel(s)
    if (status === 'Dalam Stok') return s.warehouse?.name || 'Gudang'
    if (status === 'Sudah Keluar')
      return s.ownerpartnership?.name || s.owner?.name || '-'
    if (status === 'Hapus - Jual') return parseExtraData(s)?.buyerName || '-'
    return 'Penyesuaian stok'
  }

  return { getStatusLabel, getDestinationName }
}

// ---------------------------------------------------------------------------
// Schema helpers
// ---------------------------------------------------------------------------
export const schemaKey = (p) => `${p.itemId}__${p.schemaLabel ?? ''}`

// Standard input of one material for the process that made the product
export const standardFor = (schema, product, b) => {
  if (schema?.status !== 'ok' || !product) return null
  if (!unitMatches(schema.outputUnit, product.unit)) return null
  const input = schema.inputs.find(
    (i) => b.itemId != null && Number(i.itemId) === Number(b.itemId),
  )
  if (!input || !input.reqAmount || !unitMatches(input.unit, b.unit))
    return null
  return (
    (input.reqAmount / schema.outputAmount) *
    (product.processQty ?? product.qty)
  )
}

const stepSchemaOf = (schemas, pr) => {
  if (SYSTEM_METHODS.includes(pr.label)) return null
  const sc = schemas[`${pr.itemId}__${pr.label}`]
  return sc?.status === 'ok' && sc.exact ? sc : null
}

// A step whose process has a production schema
export const stepHasSchema = (schemas, step) =>
  step.processes.some((pr) => !!stepSchemaOf(schemas, pr))

// Standard input of a material for the step it went into: the step's
// schema (reqAmount / output amount) times what that step produced
export const stepStandard = (schemas, step, b) => {
  for (const pr of step.processes) {
    const sc = stepSchemaOf(schemas, pr)
    if (!sc || !unitMatches(sc.outputUnit, pr.unit)) continue
    const input = sc.inputs.find(
      (i) =>
        b.itemId != null &&
        Number(i.itemId) === Number(b.itemId) &&
        unitMatches(i.unit, b.unit),
    )
    if (input?.reqAmount && pr.outputQty > 0) {
      return (input.reqAmount / sc.outputAmount) * pr.outputQty
    }
  }
  return null
}

// "1 Kg Kopi Green Beans → 0,85 Kg Kopi Roasted Beans" parts of a step
export const stepSchemas = (schemas, step) =>
  step.processes
    .map((pr) => ({ pr, sc: stepSchemaOf(schemas, pr) }))
    .filter(({ sc }) => sc)

export const verdictOf = (usage) => {
  if (usage == null) return null
  if (usage <= 100) return 'Sesuai skema'
  if (usage <= 120) return 'Lebih boros dari skema'
  return 'Jauh lebih boros dari skema'
}

// Loss of the process that made the product, compared with its schema
export const processComparison = (product, schema) => {
  const { inputs, bahanTotal, processQty, lossPct } = product
  if (!inputs.length || lossPct == null) return null
  const standards = inputs.map((b) => standardFor(schema, product, b))
  const hasFullStandard = standards.every((v) => v)
  const standardTotal = standards.reduce((t, v) => t + (v || 0), 0)
  const usage = hasFullStandard
    ? Math.round((bahanTotal / standardTotal) * 100)
    : null
  return {
    lossQty: bahanTotal - processQty,
    expectedLossPct: hasFullStandard
      ? Math.round(((standardTotal - processQty) / standardTotal) * 100)
      : null,
    usage,
    verdict: verdictOf(usage),
  }
}

// What a traced inventory took from its parent. For the same item and unit
// (split, transfer, ...) that is its own starting stock: `amountUsed` can be
// off there (e.g. 6 while the piece started with 5). For another item
// (production) it is the material used, `amountUsed`.
const takenFrom = (parent, link) =>
  itemKeyOf(link.s) === itemKeyOf(parent) && Number(link.s.initialAmount) > 0
    ? Number(link.s.initialAmount)
    : link.used

// ---------------------------------------------------------------------------
// Products: one entry per finished good (inventory of the lot)
// ---------------------------------------------------------------------------
export const buildProducts = (
  inventories,
  {
    isBackward,
    summaryInvMap,
    sourceById = new Map(),
    userId,
    lotCode,
    schemas,
  },
) => {
  const { getStatusLabel, getDestinationName } = makeStatusHelpers(userId)

  const inventoryMap = new Map()
  inventories.forEach((inv) => inventoryMap.set(Number(inv?.id), inv))

  const resolveSummary = (sid) => {
    const summary = summaryInvMap.get(Number(sid))
    if (summary) return summary
    const fallback = inventoryMap.get(Number(sid))
    if (!fallback) return null
    return {
      id: fallback.id,
      amount: fallback.amount,
      initialAmount: fallback.initialAmount,
      unit: fallback.unit,
      ownerUserId: fallback.ownerUserId,
      item: fallback.item,
      warehouse: fallback.warehouse || null,
      owner: fallback.owner || null,
      ownerpartnership: fallback.ownerpartnership || null,
      extraData: fallback.extraData || null,
    }
  }

  return inventories.map((inv) => {
    const unit = inv.unit || inv.item?.unit || ''
    // The API already replaces `amount` with the event amount
    // (processing report / seller fill), i.e. what this lot produced
    const qty = Number(inv.amount) || 0
    const allLinks = (inv.summaryInvIds || [])
      .map((sid) => {
        const s = resolveSummary(sid)
        if (!s) return null
        const relation = (inv.summaryInvData || []).find(
          (d) => Number(d.id) === Number(sid),
        )
        const used = Number(relation?.amountUsed ?? linkedAmount(s)) || 0
        const isSelf = Number(sid) === Number(inv.id)
        return { s, relation, used, isSelf }
      })
      .filter(Boolean)

    // FORWARD: its direct children, plus itself for the remaining stock
    const links = isBackward
      ? allLinks
      : allLinks.filter(
          ({ s, isSelf }) =>
            isSelf ||
            !s.parentInventoryIds ||
            parseIds(s.parentInventoryIds).includes(Number(inv.id)),
        )
    // FORWARD: the API also lists this inventory itself, with its current
    // owner (a transfer moves the same inventory to the receiver)
    const selfLink = links.find((l) => l.isSelf)

    // BACKWARD: materials summed per item and unit, with their Lot IDs
    const groupMaterials = (list) => {
      const totals = new Map()
      const lots = new Map()
      const ids = new Map()
      const firstSources = new Map()
      // schema amount per material, only when every piece has one
      const standards = new Map()
      const estimates = new Set()
      list.forEach(({ s, used, standard, estimated }) => {
        const itemId = s.item?.id ?? s.itemId
        const name = s.item?.nameItem || '-'
        const materialUnit = unitOf(s)
        addToBreakdown(totals, {
          itemId,
          name,
          unit: materialUnit,
          amount: used,
        })
        const key = `${itemId ?? name}__${normalizeUnit(materialUnit)}`
        if (!lots.has(key)) lots.set(key, new Set())
        if (!ids.has(key)) ids.set(key, [])
        const lot = s.lotCode || s.lotId
        if (lot) lots.get(key).add(lot)
        ids.get(key).push(Number(s.id))
        if (estimated) estimates.add(key)
        if (standard !== undefined) {
          const prev = standards.has(key) ? standards.get(key) : 0
          standards.set(
            key,
            prev == null || standard == null ? null : prev + standard,
          )
        }
        // Stock before any split (e.g. 455 Kg split down to 4,20 Kg);
        // pieces of the same source count it once
        if (!firstSources.has(key)) firstSources.set(key, new Map())
        const first = firstSourceOf(s, (pid) => nodeById.get(pid)?.s)
        firstSources.get(key).set(Number(first.id), first)
      })
      return Array.from(totals.entries()).map(([key, b]) => ({
        ...b,
        key,
        lots: Array.from(lots.get(key) || []),
        ids: ids.get(key) || [],
        // undefined: not known here (the page uses the step's schema)
        ...(standards.has(key) && { standard: standards.get(key) }),
        // amount guessed without a schema (see sourceAmounts)
        estimated: estimates.has(key),
        firstStock: Array.from(firstSources.get(key)?.values() || []).reduce(
          (t, x) => t + (Number(x.initialAmount) || 0),
          0,
        ),
      }))
    }

    // BACKWARD: the API returns every ancestor, all levels mixed. Rebuild
    // the chain from the original materials down to this finished good:
    // depth 1 = direct parents, 2 = their parents, ...
    const nodeById = new Map(allLinks.map((l) => [Number(l.s.id), l]))
    const tracedParents = (value) =>
      parseIds(value).filter((pid) => nodeById.has(pid))
    const depthById = new Map()
    let frontier = tracedParents(inv.parentInventoryIds)
    for (let depth = 1; frontier.length && depth <= 100; depth += 1) {
      const next = []
      frontier.forEach((pid) => {
        if (depthById.has(pid)) return
        depthById.set(pid, depth)
        next.push(...tracedParents(nodeById.get(pid).s.parentInventoryIds))
      })
      frontier = next
    }
    // Ancestors that cannot be placed in the chain go on the first level
    allLinks.forEach(({ s }) => {
      if (!depthById.has(Number(s.id))) depthById.set(Number(s.id), 1)
    })
    const maxDepth = Math.max(0, ...depthById.values())
    const steps = []
    for (let depth = maxDepth; depth >= 1; depth -= 1) {
      const list = allLinks.filter(
        ({ s }) => depthById.get(Number(s.id)) === depth,
      )
      if (!list.length) continue
      // The inventories made from this step (one level closer to the
      // finished good) tell which process and schema was used
      const stepIds = new Set(list.map(({ s }) => Number(s.id)))
      const children =
        depth === 1
          ? [inv]
          : allLinks
              .filter(({ s }) => depthById.get(Number(s.id)) === depth - 1)
              .map(({ s }) => s)
      const processes = new Map()
      children.forEach((child) => {
        const parentLink = tracedParents(child.parentInventoryIds)
          .filter((pid) => stepIds.has(pid))
          .map((pid) => nodeById.get(pid))
          .find((l) => l.relation?.createMethodLabel)
        const label = parentLink?.relation?.createMethodLabel
        const itemId = child.item?.id ?? child.itemId
        if (!label || itemId == null) return
        const key = `${itemId}__${label}`
        const produced = child === inv ? qty : Number(child.initialAmount) || 0
        const current = processes.get(key)
        if (current) {
          current.outputQty += produced
        } else {
          processes.set(key, {
            itemId,
            label,
            unit: unitOf(child),
            outputQty: produced,
          })
        }
      })
      steps.push({
        depth,
        materials: groupMaterials(list),
        methods: [
          ...new Set(
            list.map((l) => l.relation?.createMethodLabel).filter(Boolean),
          ),
        ],
        processes: Array.from(processes.values()),
      })
    }

    // BACKWARD view: one step from the first-place sources to this item
    const rawSources = isBackward
      ? sourceAmounts(
          inv,
          qty,
          new Map(
            allLinks.filter((l) => !l.isSelf).map((l) => [Number(l.s.id), l.s]),
          ),
          (id) => nodeById.get(Number(id))?.relation,
          schemas,
        ).map(({ s, amount, standard, estimated }) => ({
          s,
          used: amount,
          standard,
          estimated,
        }))
      : []
    // (item, process) of every production step behind those amounts, so the
    // page can load their schemas
    const sourceSchemas = isBackward
      ? [inv, ...allLinks.map((l) => l.s)]
          .map((node) => ({
            itemId: node.item?.id ?? node.itemId,
            label: tracedParents(node.parentInventoryIds)
              .map((pid) => nodeById.get(pid).relation?.createMethodLabel)
              .find((m) => m && !SYSTEM_METHODS.includes(m)),
          }))
          .filter(({ itemId, label }) => itemId != null && label)
      : []
    const lastStep = steps.find((step) => step.depth === 1) || null
    const chainMethods = [
      ...new Set(
        steps.flatMap((step) =>
          step.methods.filter((m) => !SYSTEM_METHODS.includes(m)),
        ),
      ),
    ]
    const sourceStep =
      lastStep && rawSources.length
        ? {
            ...lastStep,
            materials: groupMaterials(rawSources),
            methods: chainMethods.length ? chainMethods : lastStep.methods,
            // every process of the chain, so each source finds its schema
            processes: steps.flatMap((step) => step.processes),
          }
        : lastStep

    // The process that made this item: walk up while the parent is the
    // same item (split, transfer, ...) until the step that changed the
    // item. Loss and the schema are measured on that step.
    let producer = { s: inv, isSelf: true }
    for (let guard = 0; guard < 100; guard += 1) {
      const parents = tracedParents(producer.s.parentInventoryIds).map((pid) =>
        nodeById.get(pid),
      )
      if (
        parents.length === 1 &&
        itemKeyOf(parents[0].s) === itemKeyOf(producer.s)
      ) {
        producer = parents[0]
      } else {
        break
      }
    }
    const processLinks = tracedParents(producer.s.parentInventoryIds).map(
      (pid) => nodeById.get(pid),
    )
    const inputs = groupMaterials(processLinks)
    const processQty = producer.isSelf
      ? qty
      : Number(producer.s.initialAmount) || 0
    const processLabel = processLinks.find((l) => l.relation?.createMethodLabel)
      ?.relation?.createMethodLabel

    const bahanTotal = inputs.reduce((t, b) => t + b.amount, 0)
    const sameUnit =
      inputs.length > 0 &&
      inputs.every((b) => normalizeUnit(b.unit) === normalizeUnit(unit))
    const lossPct =
      sameUnit && bahanTotal > 0
        ? Math.round(((bahanTotal - processQty) / bahanTotal) * 100)
        : null
    // BACKWARD: the materials of "Jejak bahan sumber"
    const jenisBahan = sourceStep
      ? sourceStep.materials.length
      : new Set(allLinks.map(({ s }) => itemKeyOf(s))).size

    // FORWARD: when only part of a stock was sold, the sold part is split
    // off as a new inventory (the one in this lot) and the rest stays in
    // the inventory it was split from (same item, outside this lot)
    const sourceStocks = []
    if (!isBackward) {
      let current = inv
      for (let guard = 0; guard < 100; guard += 1) {
        const parents = parseIds(current.parentInventoryIds)
          .map((pid) => sourceById.get(pid))
          .filter(Boolean)
        if (parents.length !== 1 || itemKeyOf(parents[0]) !== itemKeyOf(inv))
          break
        current = parents[0]
        if (
          getStatusLabel(current) === 'Dalam Stok' &&
          Number(current.amount) > 0
        ) {
          sourceStocks.push(current)
        }
      }
    }

    // FORWARD: every process after this inventory as a tree, one node per
    // inventory (split, production, transfer, ...) down to where it ended
    // up, so nothing is merged away
    const forwardTree = []
    if (!isBackward) {
      const seen = new Set([Number(inv.id)])
      const nodeOf = (id) =>
        Number(id) === Number(inv.id) ? inv : nodeById.get(Number(id))?.s
      const toNode = (link) => {
        const { s, relation } = link
        // Without a traced parent it hangs under the lot in the tree, so
        // the lot is what it came from
        const parent =
          parseIds(s.parentInventoryIds).map(nodeOf).find(Boolean) || inv
        const isMove = itemKeyOf(parent) === itemKeyOf(s)
        seen.add(Number(s.id))
        const status = getStatusLabel(s)
        // same statuses as the summary: goods made into another item that
        // are still with the user are "Diproses"
        const statusKey =
          status === 'Dalam Stok' && itemKeyOf(s) !== itemKeyOf(inv)
            ? 'Diproses'
            : status
        return {
          id: s.id,
          name: s.item?.nameItem || '-',
          code: s.ownerCode || null,
          lot: s.lotCode || s.lotId || '-',
          // A move (same item) counts in its own unit; a production step
          // counts the material it used, so in the unit of that material
          unit: isMove
            ? unitOf(s) || unit
            : unitOf(parent) || unitOf(s) || unit,
          qty: takenFrom(parent, link),
          // what is still in the user's warehouse now (it may have given
          // part of what it took to the next step), in its own unit
          stockNow: status === 'Dalam Stok' ? Number(s.amount) || 0 : null,
          stockUnit: unitOf(s) || unit,
          // production: what was made (the whole inventory, it may also
          // contain material from outside this lot)
          output: isMove ? null : Number(s.initialAmount) || null,
          method: relation?.createMethodLabel || null,
          statusKey,
          status: STATUS_LABELS[statusKey] || statusKey,
          destination: getDestinationName(s),
          // eslint-disable-next-line no-use-before-define
          children: childrenOf(Number(s.id)),
        }
      }
      const childrenOf = (parentId) =>
        allLinks
          .filter(
            ({ s, isSelf }) =>
              !isSelf &&
              !seen.has(Number(s.id)) &&
              parseIds(s.parentInventoryIds).includes(parentId),
          )
          .map(toNode)
      forwardTree.push(...childrenOf(Number(inv.id)))
      // Traced inventories whose parent is not in the trace still belong to
      // this lot: show them (with their descendants) at the top level
      allLinks
        .filter(({ s, isSelf }) => !isSelf)
        .forEach((link) => {
          if (!seen.has(Number(link.s.id))) forwardTree.push(toNode(link))
        })
    }

    // FORWARD: destinations, grouped per status
    // FORWARD: where the goods of this inventory are now, over the whole
    // trace (not only its direct children). Each inventory keeps what it
    // took minus what its children took, at its own status / owner.
    //   - same item (split, transfer, ...): counted 1:1 in the lot's unit
    //   - another item (production): "Diproses jadi barang lain" with the
    //     lot amount it used; its own subtree is then followed in its own
    //     unit (`downstream`), so a receiver shows what was really sent
    //   - an inventory whose parent is not in the trace still belongs to
    //     this lot and is taken as a direct child of it
    // Only non-downstream entries are in the lot's unit and add up to it.
    const allocations = []
    if (!isBackward) {
      const rootId = Number(inv.id)
      const tracedIds = new Set(allLinks.map(({ s }) => Number(s.id)))
      const isOrphan = (s) =>
        !parseIds(s.parentInventoryIds).some(
          (pid) => pid === rootId || tracedIds.has(pid),
        )
      const childLinks = (id) =>
        allLinks.filter(
          ({ s, isSelf }) =>
            !isSelf &&
            (parseIds(s.parentInventoryIds).includes(id) ||
              (id === rootId && isOrphan(s))),
        )
      const visited = new Set()

      // took: what this inventory took, in its own unit; downstream: it was
      // made from the lot by production; knownStock: its `amount` is the
      // current stock (false for the lot itself when only the event amount
      // is known)
      const walk = (link, took, downstream, knownStock) => {
        const id = Number(link.s.id)
        if (visited.has(id)) return
        visited.add(id)
        let given = 0
        childLinks(id).forEach((child) => {
          if (visited.has(Number(child.s.id))) return
          const taken = takenFrom(link.s, child)
          given += taken
          if (itemKeyOf(child.s) === itemKeyOf(link.s)) {
            walk(child, taken, downstream, true)
            return
          }
          const method = child.relation?.createMethodLabel
          allocations.push({
            status: 'Diproses',
            id: child.s.id,
            name: `${child.s.item?.nameItem || '-'}${method ? ` (${method})` : ''}`,
            receiver: null,
            code: child.s.ownerCode || null,
            lot: child.s.lotCode || child.s.lotId || '-',
            qty: child.used,
            unit: unitOf(link.s) || unit,
            downstream,
          })
          // what was made, followed in its own unit
          walk(child, Number(child.s.initialAmount) || child.used, true, true)
        })

        const status = getStatusLabel(link.s)
        let left = took - given
        // In the user's own warehouse never more than its current stock
        // (the rest went somewhere outside the trace, e.g. an adjustment)
        if (status === 'Dalam Stok' && knownStock) {
          left = Math.min(left, Number(link.s.amount) || 0)
        }
        const kept = Math.max(0, left)
        if (kept <= 0.0001) return

        const ownStock = status === 'Dalam Stok'
        const destination = getDestinationName(link.s)
        allocations.push({
          status,
          id: link.s.id,
          name: !downstream
            ? id === rootId && ownStock
              ? `${destination} (sisa lot ini)`
              : destination
            : `${destination} · ${link.s.item?.nameItem || '-'}`,
          // who has it now (receiver, buyer or warehouse), without the item
          receiver: destination,
          code: link.s.ownerCode || null,
          lot: link.s.lotCode || link.s.lotId || lotCode || '-',
          qty: kept,
          unit: unitOf(link.s) || unit,
          // still in the user's own stock: not a destination
          isStock: ownStock,
          downstream,
        })
      }
      walk(selfLink || { s: inv }, qty, false, !!selfLink)
    }

    const destGroups = STATUS_ORDER.map((status) => {
      const items = allocations
        .filter((x) => x.status === status)
        .concat(
          status === 'Dalam Stok'
            ? sourceStocks.map((src) => ({
                id: src.id,
                name: `${getDestinationName(src)} (stok asal, belum dijual)`,
                code: src.ownerCode || null,
                lot: src.lotCode || src.lotId || '-',
                qty: Number(src.amount) || 0,
                unit: unitOf(src) || unit,
                isStock: true,
                // outside this lot (the rest of the split source)
                fromSource: true,
              }))
            : [],
        )
        .sort((a, b) => b.qty - a.qty)
      return {
        status,
        label: STATUS_LABELS[status],
        items,
        total: items.reduce((t, x) => t + x.qty, 0),
        // per unit: goods made from the lot can have another unit
        totals: Array.from(
          items
            .reduce((map, x) => {
              const key = normalizeUnit(x.unit)
              const row = map.get(key) || { unit: x.unit, amount: 0 }
              row.amount += x.qty
              return map.set(key, row)
            }, new Map())
            .values(),
        ),
      }
    }).filter((g) => g.items.length > 0)

    return {
      id: inv.id,
      itemId: inv.item?.id ?? inv.itemId,
      name: inv.item?.nameItem || '-',
      code: inv.ownerCode || null,
      traceCode: inv.traceCode || null,
      lot: inv.lotCode ?? lotCode ?? '-',
      unit,
      qty,
      // what the user still has of this item: everything in "Masih di
      // Gudang" (own stock of this inventory, pieces split off that are still
      // in a warehouse, and the rest of the stock it was split from)
      // (only this lot's own item, not goods made from it)
      remaining: (
        destGroups.find((g) => g.status === 'Dalam Stok')?.items || []
      )
        .filter((x) => !x.downstream)
        .reduce((t, x) => t + x.qty, 0),
      schemaLabel: isBackward
        ? processLabel
        : links.find((l) => l.relation?.createMethodLabel)?.relation
            ?.createMethodLabel,
      steps,
      // BACKWARD view: one step from the first-place sources to this item,
      // with the stock of each material before any split
      lastStep: sourceStep,
      sourceSchemas,
      jenisBahan,
      inputs,
      processQty,
      processLabel,
      producedBySplit: !producer.isSelf,
      bahanTotal,
      lossPct,
      destGroups,
      forwardTree,
      // how many receivers (sent or sold), counted once each like the
      // Penerima card: one inventory can appear as "Diproses" and as what
      // was sent from it, and several inventories can go to one receiver
      destCount: new Set(
        destGroups
          .filter((g) => RECEIVED_STATUSES.includes(g.status))
          .flatMap((g) => g.items.map((x) => x.receiver || x.name)),
      ).size,
    }
  })
}

// FORWARD "Barang Jadi": where each inventory of this lot went, per item
// and unit of the lot. Uses the same destinations as the page (direct
// children with their Jumlah Dipakai, plus the lot's own remaining stock),
// so deeper descendants are never counted again. Stock of the inventory a
// sold part was split from belongs to another lot and is left out.
export const destinationUsage = (products) => {
  const totals = new Map()
  ;(products || []).forEach((p) =>
    (p.destGroups || []).forEach((g) =>
      g.items
        // lot unit only: goods made from the lot are already in "Diproses"
        .filter((x) => !x.fromSource && !x.downstream && Number(x.qty) > 0)
        .forEach((x) =>
          addToBreakdown(totals, {
            itemId: p.itemId,
            name: p.name,
            unit: x.unit || p.unit,
            amount: Number(x.qty),
          }),
        ),
    ),
  )
  return Array.from(totals.values())
}

// FORWARD "Penerima": who received goods of this lot (sent or sold), per
// receiver and unit. Same destinations as the page, so goods a receiver
// split again later are not counted a second time.
export const recipientUsage = (products) => {
  const totals = new Map()
  ;(products || []).forEach((p) =>
    (p.destGroups || [])
      .filter((g) => RECEIVED_STATUSES.includes(g.status))
      .forEach((g) =>
        g.items
          .filter((x) => Number(x.qty) > 0)
          .forEach((x) =>
            addToBreakdown(totals, {
              name: x.receiver || x.name,
              unit: x.unit || p.unit,
              amount: Number(x.qty),
            }),
          ),
      ),
  )
  return Array.from(totals.values()).sort((a, b) => b.amount - a.amount)
}

// Every schema (output item + process label) the products need
export const requiredSchemaKeys = (products) => {
  const keys = new Map()
  products.forEach((p) => {
    if (p.itemId && p.schemaLabel) {
      keys.set(schemaKey(p), { itemId: p.itemId, label: p.schemaLabel })
    }
    p.steps.forEach((step) =>
      step.processes
        .filter((pr) => !SYSTEM_METHODS.includes(pr.label))
        .forEach((pr) =>
          keys.set(`${pr.itemId}__${pr.label}`, {
            itemId: pr.itemId,
            label: pr.label,
          }),
        ),
    )
  })
  return keys
}
